import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import PhysicianBadge from 'components/Physician/PhysicianBadge';
import DefaultTextField from 'components/Utils/DefaultTextField';
import AppointmentUtils from 'services/AppointmentUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import PatientBadge from 'components/Patient/PatientBadge';
import CalendarUtils from 'services/CalendarUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import './style.scss';
import PatientUtils from 'services/PatientUtils';
import Icon from 'components/Utils/NewIcon';
import EditAppointment from 'components/HealthRecords/Appointments/EditAppointment';
import { PrintLabelButton } from 'scenes/HomeStaff/ClinicQueue/QueueBadge';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { AddressElement, OHIPElement } from 'components/Patient/PatientInfoRow';
import useFetchPatients from 'hooks/useFetchPatients';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { isResponseError } from 'careand-redux/api/SerializedReponseError';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';

const dateFormat = 'Do MMM YYYY';
const timeFormat = 'h:mm A';

const STATUS = {
  no_show: 'no_show',
  cancelled: 'cancelled',
  checked_in: 'checked_in',
  questionnaire: 'questionnaire',
  split: 'split',
  student: 'student',
};

const DEFAULT_ARRAY = [];

const AdminAppointmentDetails = (props) => {
  const dispatch = useAppDispatch();

  const [fullEditScreen, setFullEditScreen] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState();
  const [appointment, setAppointment] = useState(props.appointment);
  const [splitConfirmationDialog, showSplitConfirmationDialog] = useState();

  const queue = useAppSelector((state) => state.queue.elements);
  const patient = useAppSelector((state) => state.patients.byId[appointment.patient_id]);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const physician = physicians[appointment.physician_id];
  const admins = useAppSelector((state) => state.admins.byId);

  const queueElement = useMemo(() => {
    return queue.find(({ appointment_id }) => appointment_id === appointment.id);
  }, [queue, appointment]);

  const canCancel = AppointmentUtils.isActive(appointment);

  let { status, patient_ids = DEFAULT_ARRAY } = appointment;
  let { scheduled, no_show } = AppointmentUtils.getAppointmentStatus();

  let [loadingPatients] = useFetchPatients(undefined, patient_ids);

  useEffect(() => {
    setAppointment(props.appointment);
  }, [props.appointment]);

  const isVideo = AppointmentUtils.isTelemedicineAppointment(appointment);
  const isClinic = AppointmentUtils.isClinicAppointment(appointment);

  let canCheckIn = (isClinic || isVideo) && (status === scheduled || status === no_show);

  const canSplit =
    AppointmentUtils.isClinicAppointment(appointment) &&
    AppointmentUtils.getDuration(appointment) >= 0.5;

  let day = CalendarUtils.format(appointment.date, dateFormat);
  let time = CalendarUtils.format(appointment.date, timeFormat);

  async function updateStatus(status) {
    setLoadingStatus(status);
    let result = await dispatch(
      api.Appointments.updateAppointmentStatus({ id: appointment.id, status }),
      {
        errorTitle: 'Appointment',
      },
    );
    setLoadingStatus();
    if (result) setAppointment(result);
    return result;
  }

  async function cancelAppointment() {
    return updateStatus(STATUS.cancelled);
  }

  async function checkInPatient() {
    let result = await updateStatus(STATUS.checked_in);
    if (result && patient) PatientUtils.showNotificationIfOhipInvalid(patient);
  }

  async function sendQuestionnaire() {
    setLoadingStatus(STATUS.questionnaire);
    await dispatch(api.Resource.sendQuestionnaireBySMS({ id: appointment.id }), {
      title: 'Send Questionnaire by SMS',
      successText: 'Questionnaire sent successfully!',
    });
    setLoadingStatus(undefined);
  }

  async function markAppointmentAsNoShow() {
    return updateStatus(STATUS.no_show);
  }

  async function splitAppointment(confirmSlot = false, insertBefore = true, skipPayment) {
    setLoadingStatus(STATUS.split);
    let response = await dispatch(
      api.Appointments.splitAppointment({
        id: appointment.id,
        confirmSlot,
        insertBefore,
        skipPayment,
      }),
      {
        title: 'Split Appointment',
        successText: 'Appointment split successfully!',
        meta: true,
      },
    );

    setLoadingStatus(undefined);

    if (AppointmentUtils.responseNeedsConfirmation(response.status)) {
      if (isResponseError(response.error)) {
        showSplitConfirmationDialog({
          message: response.error?.response?.jsonBodyMessage,
          insertBefore,
          confirmType: response.status === 402 ? 'payment' : 'slot',
        });
      }
    } else {
      showSplitConfirmationDialog();
    }
  }

  let width = '470px';

  const isBlocked = appointment.type === AppointmentUtils.getAppointmentTypes().blocked;

  const hasPatients = (appointment.patient_ids || []).length > 0;
  const showPatients = isBlocked && hasPatients;

  if (showPatients || fullEditScreen) {
    width = '850px';
  }

  const questionnaireHidden = !AppointmentUtils.isClinicAppointment(appointment);
  const questionnaireDisabled =
    appointment.status !== AppointmentUtils.getAppointmentStatus().checked_in;

  const statusHistory = useMemo(() => {
    return AppointmentUtils.getStatusHistory(appointment, physicians, admins, patient);
  }, [appointment, physicians, admins, patient]);

  return (
    <div className="admin-appointment-details">
      <div className="appointment-view" style={{ width }}>
        {fullEditScreen && (
          <EditAppointment
            appointment={appointment}
            showPatient={true}
            defaultShowCalendar={false}
            onClose={(appointment) => {
              if (appointment) setAppointment(appointment);
              setFullEditScreen(false);
            }}
          />
        )}

        {!fullEditScreen && (
          <>
            <div className="data scroll-list">
              <div className="icon-wrappers">
                {!!patient && (
                  <>
                    <div className="icon-wrapper ohip-icon pr-2">
                      <OHIPElement patient={patient} showSeparator={false} />
                    </div>
                    <div className="icon-wrapper address-icon pr-2">
                      <AddressElement patient={patient} className="blue" />
                    </div>
                  </>
                )}
                <div
                  className="icon-wrapper edit-icon-wrapper"
                  onClick={(event) => {
                    setFullEditScreen(true);
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <Icon height="20px" src="pencil" alt="Edit Appointment" showTooltip={true} />
                </div>
                <div className="icon-wrapper">
                  <PrintLabelButton appointment={appointment} className="grey" />
                </div>
                <Icon
                  src="circle-info"
                  alt={statusHistory}
                  showTooltip={true}
                  tooltipPlace="bottom"
                  width="25px"
                  style={{ marginLeft: '5px' }}
                />
              </div>

              {appointment.patient_id !== undefined && (
                <PatientBadge patient={patient} goToProfile={true} />
              )}
              <PhysicianBadge physician={physician} />

              <TextField name="type" value={appointment.type} />

              <TextField name="status" value={appointment.status} />

              <TextField name="day" value={day} />
              <TextField name="time" value={time} />

              {!!appointment.telemedicine_method && (
                <TextField
                  name="telemedicine_method"
                  placeholder="Telemedicine Method"
                  value={appointment.telemedicine_method}
                />
              )}

              {!!appointment.concern_text && (
                <TextField name="concern" value={appointment.concern_text} />
              )}

              {!!appointment.notes && (
                <>
                  <span className="small-title">Notes</span>
                  <pre>{appointment.notes}</pre>
                </>
              )}

              {!!appointment.complaints && (
                <>
                  <span className="small-title">
                    {appointment.type === AppointmentUtils.getAppointmentTypes().blocked
                      ? 'Reason'
                      : 'Complaints'}
                  </span>
                  <pre>{appointment.complaints}</pre>
                </>
              )}

              <div className="buttons-wrapper flex">
                {canSplit && (
                  <>
                    <ConfirmationButton
                      name="Split with RPN"
                      hide={!canSplit}
                      disabled={loadingStatus}
                      loading={loadingStatus === STATUS.split}
                      icon="split"
                      style={{ width: '45%' }}
                      confirmation={{
                        message:
                          'Should the RPN see the patient before or after the main appointment?',
                        confirmLabel: 'After',
                        cancelLabel: 'Before',
                        cancelClassName: 'white',
                        confirmClassName: 'white',
                        onCancel: () => splitAppointment(false, true),
                      }}
                      onClick={() => splitAppointment(false, false)}
                    />
                    <div style={{ width: '45%' }}>&nbsp;</div>
                  </>
                )}
                <DefaultButton
                  name="Questionnaire SMS"
                  hide={questionnaireHidden}
                  disabled={questionnaireDisabled || loadingStatus}
                  loading={loadingStatus === STATUS.questionnaire}
                  onClick={sendQuestionnaire}
                  icon="table-list"
                  style={{ width: '45%' }}
                />
                <DefaultButton
                  name="Check-in"
                  hide={!isClinic && !isVideo}
                  disabled={!canCheckIn || loadingStatus}
                  loading={loadingStatus === STATUS.checked_in}
                  onClick={checkInPatient}
                  icon="door-open"
                  style={{ width: '45%' }}
                />
                <ConfirmationButton
                  name="No-show"
                  className="blue"
                  confirmation={{
                    message: 'Are you sure you want to mark the appointment as no-show?',
                    cancelLabel: 'No, leave as is',
                    confirmLabel: 'Yes, mark it',
                  }}
                  disabled={!canCancel || loadingStatus}
                  hide={!AppointmentUtils.isClinicAppointment(appointment)}
                  loading={loadingStatus === STATUS.no_show}
                  onClick={markAppointmentAsNoShow}
                  style={{ width: '45%' }}
                />
                <ConfirmationButton
                  name="Cancel"
                  className="red"
                  confirmation={{
                    message: 'Are you sure you want to cancel the appointment?',
                    cancelLabel: 'No, leave as is',
                    confirmLabel: 'Yes, cancel it',
                  }}
                  disabled={!canCancel || loadingStatus}
                  loading={loadingStatus === STATUS.cancelled}
                  onClick={cancelAppointment}
                  icon="rectangle-xmark"
                  style={{ width: '45%' }}
                />
              </div>
            </div>
            {showPatients && (
              <div className="patients scroll-list">
                <span className="small-title">Patients</span>
                <ProgressMessage show={loadingPatients} />
                {!loadingPatients &&
                  (appointment.patient_ids || []).map((id) => {
                    return <PatientBadge id={id} goToProfile={true} key={id} />;
                  })}
              </div>
            )}
          </>
        )}
      </div>
      {splitConfirmationDialog && (
        <ConfirmationDialog
          title="Confirmation Needed"
          message={splitConfirmationDialog?.message}
          open={!!splitConfirmationDialog}
          onConfirm={() => splitAppointment(true, splitConfirmationDialog?.insertBefore)}
          onCancel={() => showSplitConfirmationDialog()}
          loadingState={loadingStatus === STATUS.split}
          additionalButton={
            <DefaultButton
              name="Book With No Payment"
              id="alert-dialog-skip"
              loading={loadingStatus === STATUS.split}
              onClick={() => splitAppointment(true, splitConfirmationDialog?.insertBefore, true)}
              style={{ marginRight: '20px', width: '190px' }}
              hide={splitConfirmationDialog.confirmType !== 'payment'}
            />
          }
        />
      )}
    </div>
  );
};

const TextField = ({ name, placeholder, value, margin = true, style = {} }) => (
  <DefaultTextField
    name={name}
    label={placeholder || PerformanceUtils.capitalizeFirstLetter(name)}
    style={{
      width: 'calc(50% - 20px)',
      marginRight: margin ? '20px' : 0,
      ...style,
    }}
    value={value}
    disabled={true}
  />
);

AdminAppointmentDetails.propTypes = {
  appointment: PropTypes.object,
  onClose: PropTypes.func,
};

export default AdminAppointmentDetails;
